/**
 * Палитра графика.
 *
 * Повторяет токены интерфейса: график — не вставка со своим вкусом, а
 * его продолжение. Зелёный и красный здесь семантика — рост и падение,
 * LONG и SHORT, — поэтому акцент интерфейса намеренно другой, синий:
 * маркеры сигналов не должны спорить с элементами управления.
 *
 * Сетка почти не видна: она нужна, чтобы глаз держал уровень, а не
 * чтобы её разглядывали. Свечи спокойные — на чёрном фоне яркая заливка
 * бьёт по глазам через час работы.
 */

import { formatCrosshairTime, formatTick, formatTicks } from './axis-format.js';

export type ThemeName = 'dark' | 'light';

interface Palette {
  ground: string;
  surface: string;
  line: string;
  grid: string;
  ink: string;
  inkMuted: string;
  accent: string;
  up: string;
  down: string;
  upSoft: string;
  downSoft: string;
  gap: string;
}

/*
 * Палитра холста повторяет токены интерфейса.
 *
 * Значения продублированы числами намеренно: холст рисуется не CSS, а
 * Canvas — переменные оттуда ему недоступны. Дублирование обязано
 * оставаться осознанным: разошедшиеся значения дают полотно графика
 * одного оттенка внутри панели другого, и стык между ними виден как
 * линия там, где её не рисовали.
 */
const DARK: Palette = {
  ground: '#060B15',
  surface: '#101A2B',
  line: '#2A3A52',
  grid: '#121C2C',
  ink: '#EAF0F8',
  inkMuted: '#8FA0B8',
  accent: '#22D3EE',
  up: '#2EE6B0',
  down: '#FF5470',
  upSoft: 'rgba(46, 230, 176, 0.42)',
  downSoft: 'rgba(255, 84, 112, 0.42)',
  gap: 'rgba(255, 84, 112, 0.10)',
};

/**
 * Светлая палитра графика.
 *
 * Не инверсия тёмной: на белом фоне те же зелёный и красный кричат, а
 * сетка, наоборот, пропадает. Значения подобраны отдельно — иначе
 * график перестал бы читаться ровно там, где он главный.
 */
const LIGHT: Palette = {
  ground: '#FFFFFF',
  surface: '#F2F4F8',
  line: '#D6DDE7',
  grid: '#EDF0F5',
  ink: '#10141B',
  inkMuted: '#55606F',
  accent: '#1F6FEB',
  up: '#0F9D6E',
  down: '#D92C3C',
  upSoft: 'rgba(15, 157, 110, 0.42)',
  downSoft: 'rgba(217, 44, 60, 0.42)',
  gap: 'rgba(217, 44, 60, 0.10)',
};

/**
 * Действующая палитра.
 *
 * Изменяемый объект, а не константа: график создаётся один раз, а тему
 * человек переключает в любой момент. Слои разметки и маркеров читают
 * цвета отсюда — так им не нужно знать про тему вообще.
 */
export const palette: Palette = { ...DARK };

/** Переключить палитру. Возвращает опции для `chart.applyOptions`. */
export function applyTheme(theme: ThemeName): ReturnType<typeof buildOptions> {
  Object.assign(palette, theme === 'light' ? LIGHT : DARK);
  return buildOptions();
}

function buildOptions() {
  return {
  layout: {
    background: { color: palette.ground },
    textColor: palette.inkMuted,
    /*
     * Шкалы — шрифтом терминала, а не моноширинным: «87 000» читается
     * как цена, а не как распечатка, и шкала выходит уже.
     */
    fontFamily: "Onest, system-ui, -apple-system, 'Segoe UI', sans-serif",
    fontSize: 11,
    panes: { separatorColor: palette.line, separatorHoverColor: palette.accent },
    /**
     * Логотип TradingView на канве выключен.
     *
     * Lightweight Charts под Apache 2.0: отключить логотип можно, но
     * ссылка на tradingview.com должна остаться где-то в интерфейсе.
     * Она перенесена в статус-бар — см. StatusBar. Убрать её совсем
     * нельзя, не нарушив условия библиотеки.
     */
    attributionLogo: false,
  },
  grid: {
    vertLines: { color: palette.grid },
    horzLines: { color: palette.grid },
  },
  rightPriceScale: {
    borderColor: palette.line,
    scaleMargins: { top: 0.08, bottom: 0.08 },
  },
  // Числа и даты на шкалах — по-человечески (см. axis-format.ts).
  localization: {
    locale: 'ru-RU',
    tickmarksPriceFormatter: (prices: number[]) => formatTicks(prices),
    timeFormatter: (time: unknown) => (typeof time === 'number' ? formatCrosshairTime(time) : ''),
  },
  timeScale: {
    borderColor: palette.line,
    timeVisible: true,
    secondsVisible: false,
    tickMarkFormatter: (time: unknown, type: number) => (typeof time === 'number' ? formatTick(time, type) : null),
    rightOffset: 4,
  },
  crosshair: {
    mode: 0, // Normal: перекрестье следует за курсором, а не липнет к барам
    vertLine: {
      color: 'rgba(141, 150, 165, 0.45)',
      width: 1 as const,
      style: 3,
      labelBackgroundColor: palette.surface,
    },
    horzLine: {
      color: 'rgba(141, 150, 165, 0.45)',
      width: 1 as const,
      style: 3,
      labelBackgroundColor: palette.surface,
    },
  },
  autoSize: false,
  };
}

/** Опции графика для текущей палитры. */
/**
 * Настройки графика по действующей палитре.
 *
 * Функция, а не готовый объект. Объект собирался в момент импорта
 * модуля — то есть раньше, чем становилась известна тема, — и навсегда
 * оставался тёмным. Палитру дальше меняли, слои её перечитывали, а
 * подложка самого полотна так и оставалась той, с которой модуль
 * загрузился: у человека со светлой темой весь терминал был светлым, а
 * график посреди него — чёрным.
 */
export function chartOptions(): ReturnType<typeof buildOptions> {
  return buildOptions();
}

/** Опции свечей для текущей палитры: цвета берутся в момент вызова. */
export function candleColors(): {
  upColor: string;
  downColor: string;
  borderUpColor: string;
  borderDownColor: string;
  wickUpColor: string;
  wickDownColor: string;
} {
  return {
    upColor: palette.up,
    downColor: palette.down,
    borderUpColor: palette.up,
    borderDownColor: palette.down,
    wickUpColor: palette.up,
    wickDownColor: palette.down,
  };
}

export const candleSeriesOptions = candleColors();


